//@flow
import React from 'react';
import {
  Chip,
  CircularProgress,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Pagination from '@material-ui/lab/Pagination';
import {colors} from '../../../../assets/styles/colors';
import {formatDateTime} from '../../../../utils/date';
import {getTriggerExecutionStateLabel, useTriggerExecutions} from '../utils';
import {getPaginationCount} from '../../../../utils/pagination';

type Props = {|
  triggerId: ?string,
  enabled?: boolean,
|};

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
    padding: '0.75rem',
    background: '#fff',
    borderRadius: '12px',
    border: `1px solid ${colors.grey300}`,
  },
  rowHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  identifier: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: colors.grey1000,
  },
  meta: {
    fontSize: '0.8125rem',
    color: colors.grey700,
  },
  chip: {
    borderRadius: '8px',
    fontWeight: 600,
    fontSize: '0.75rem',
    height: '24px',
  },
  succeededChip: {
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
  },
  failedChip: {
    backgroundColor: '#FFEBEE',
    color: '#C62828',
  },
  pendingChip: {
    backgroundColor: '#FFF8E1',
    color: '#F57F17',
  },
  defaultChip: {
    backgroundColor: colors.grey200,
    color: colors.grey800,
  },
  empty: {
    fontSize: '0.875rem',
    color: colors.grey700,
    padding: '0.5rem 0',
  },
  loader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 0',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '0.25rem',
  },
}));

function getStateChipClass(classes: {[string]: string}, state: string): string {
  if (state === 'SUCCEEDED') return classes.succeededChip;
  if (state === 'FAILED') return classes.failedChip;
  if (state === 'PENDING' || state === 'RUNNING') return classes.pendingChip;
  return classes.defaultChip;
}

export default function TriggerExecutionHistory({
  triggerId,
  enabled = true,
}: Props) {
  const classes = useStyles();
  const {
    executions,
    loading,
    pagination,
    page,
    setPage,
  } = useTriggerExecutions(triggerId, {enabled: Boolean(enabled && triggerId)});

  if (!enabled || !triggerId) return null;

  const pageCount = getPaginationCount(pagination);

  return (
    <div className={classes.root}>
      {loading && executions.length === 0 ? (
        <div className={classes.loader}>
          <CircularProgress size={18} />
          <Typography className={classes.meta}>
            Cargando ejecuciones...
          </Typography>
        </div>
      ) : executions.length === 0 ? (
        <Typography className={classes.empty}>
          Aún no hay ejecuciones
        </Typography>
      ) : (
        <div className={classes.list}>
          {executions.map(execution => (
            <div
              key={`${execution.identifier}-${execution.executedAt}`}
              className={classes.row}
            >
              <div className={classes.rowHeader}>
                <Typography className={classes.identifier}>
                  {execution.identifier}
                </Typography>
                <Chip
                  size="small"
                  label={getTriggerExecutionStateLabel(execution.state)}
                  className={`${classes.chip} ${getStateChipClass(
                    classes,
                    execution.state
                  )}`}
                />
              </div>
              <Typography className={classes.meta}>
                Canal: {execution.ravenService || '—'}
              </Typography>
              <Typography className={classes.meta}>
                Ejecutado:{' '}
                {execution.executedAt
                  ? formatDateTime(new Date(execution.executedAt))
                  : '—'}
              </Typography>
            </div>
          ))}
        </div>
      )}

      {pageCount > 1 && (
        <div className={classes.pagination}>
          <Pagination
            count={pageCount}
            page={page + 1}
            onChange={(_, nextPage) => setPage(nextPage - 1)}
            color="primary"
            size="small"
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
}
